// Import auth service for authenticated requests
import { authService } from './authService';
import { getServerURL } from './config';

// Generation Scenes Service for CRUD operations
const SERVER_URL = getServerURL();

export const generationScenesService = {
  // Get all generation scenes
  async getAllScenes() {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
      } else {
        throw new Error(`Failed to fetch generation scenes: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation scenes:', error);
      return [];
    }
  },

  // Get deleted generation scenes
  async getDeletedScenes() {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/deleted/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
      } else {
        throw new Error(`Failed to fetch deleted generation scenes: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching deleted generation scenes:', error);
      return [];
    }
  },

  // Get generation scene by ID
  async getSceneById(id) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/${id}/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error(`Failed to fetch generation scene: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation scene:', error);
      throw error;
    }
  },

  // Get generation scenes by story slug
  async getScenesByStorySlug(slug) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/story/${slug}/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
      } else {
        throw new Error(`Failed to fetch generation scenes by story slug: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation scenes by story slug:', error);
      return [];
    }
  },

  // Create generation scene
  async createScene(sceneData) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(sceneData),
      });
      if (response.ok) {
        return await response.json();
      } else {
        let errorData = {};
        try { errorData = await response.json(); } catch {}
        throw new Error(`Failed to create generation scene: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating generation scene:', error);
      throw error;
    }
  },

  async uploadSceneAsset({ file, assetKind = 'scene', storySlug = '', sceneSlug = '', order = '' }) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    if (!file) throw new Error('No file selected for upload');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_kind', assetKind === 'mask' ? 'mask' : 'scene');
      formData.append('story_slug', storySlug || 'manual');
      formData.append('scene_slug', sceneSlug || 'scene');
      formData.append('order', String(order ?? ''));

      const token = authService.getToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${SERVER_URL}/generation-scenes/upload-asset`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Failed to upload generation scene asset: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error uploading generation scene asset:', error);
      throw error;
    }
  },

  async renderTextScenePreview(payload) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');

    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/text-preview`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(payload || {}),
      });

      if (!response.ok) {
        let errorMessage = `Failed to render text preview: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData?.error || errorData?.message || errorMessage;
        } catch (_) {
          // ignore json parsing failure for binary/error responses
        }
        throw new Error(errorMessage);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error rendering text scene preview:', error);
      throw error;
    }
  },

  async renderCoverScenePreview(payload) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');

    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/cover-preview`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(payload || {}),
      });

      if (!response.ok) {
        let errorMessage = `Failed to render cover preview: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData?.error || errorData?.message || errorMessage;
        } catch (_) {
          // ignore json parsing failure for binary/error responses
        }
        throw new Error(errorMessage);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error rendering cover scene preview:', error);
      throw error;
    }
  },

  // Update generation scene
  async updateScene(id, sceneData) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/${id}/`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(sceneData),
      });
      if (response.ok) {
        return await response.json();
      } else {
        let errorData = {};
        try { errorData = await response.json(); } catch {}
        throw new Error(`Failed to update generation scene: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating generation scene:', error);
      throw error;
    }
  },

  // Delete generation scene
  async deleteScene(id) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/${id}/`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ text: `Generation scene with ID ${id}` }),
      });
      if (response.ok) {
        return await response.json();
      } else {
        let errorData = {};
        try { errorData = await response.json(); } catch {}
        throw new Error(`Failed to delete generation scene: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting generation scene:', error);
      throw error;
    }
  },

  // Restore generation scene
  async restoreScene(id) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    try {
      const response = await fetch(`${SERVER_URL}/generation-scenes/${id}/restore/`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        return await response.json();
      } else {
        let errorData = {};
        try { errorData = await response.json(); } catch {}
        throw new Error(`Failed to restore generation scene: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error restoring generation scene:', error);
      throw error;
    }
  },
}; 
